/**
 * Created by bcole on 1/12/20.
 */
import Actions from "./Actions";

// Phases are always shown earliest-first, same order the API returns them
// in - re-sorted here too so editing a phase's dates moves it in the list
// without a round trip.
const sortPhases = (phases) => [...phases].sort((a, b) => new Date(a.startdate) - new Date(b.startdate));

/*
 * Where the page an API module resolved becomes the paging a list stores.
 *
 * The two halves call the cursor different things, and deliberately: over HTTP
 * it is `nextCursor` (the server naming what comes *after* this page - see
 * api/lib/pagination.js), while the store calls it `cursor` because by the time
 * it is held there, it is the position the next fetch starts from.
 *
 * Both names were right and the translation between them was missing. Every
 * reducer destructured `cursor` from a payload that only ever carried
 * `nextCursor`, so every list stored `cursor: null`, and usePagedList - which
 * refuses to invent a cursor it wasn't given - stopped at the first page of the
 * backlog, the bug list and the support queue. Nothing errored; the lists just
 * quietly ended. One crossing point now, rather than six.
 */
const firstPagePaging = (payload) => ({
    cursor: payload.nextCursor ?? null,
    hasMore: payload.hasMore ?? false,
    // Counted once per set of filters, so only the first page carries it.
    total: payload.total ?? null,
});

const nextPagePaging = (payload, existing) => ({
    ...existing,
    cursor: payload.nextCursor ?? null,
    hasMore: payload.hasMore ?? false,
});

const Reducer = (state, action) => {

    switch (action.type) {
        case Actions.SET_USER: {
            return {
                ...state, user: action.payload
            };
        }

        case Actions.SET_PROJECT: {
            return {
                ...state,
                ...action.payload
            };
        }

        case Actions.SET_BACKLOG: {
            return {
                ...state, backlog: action.payload
            };
        }

        case Actions.SET_PERMISSIONS: {
            return {
                ...state, permissions: action.payload
            };
        }

        case Actions.SET_DESIGN_PAGE: {
            return {
                ...state,
                designPages: {...state.designPages, [action.payload.key]: action.payload.data},
            };
        }

        // A live patch (see "entity:patched" in Project.js) or this
        // session's own save landing back through the store - finds
        // whichever key currently holds this design page's id (a page is
        // only ever keyed here if this session has actually visited it -
        // see designPages' comment in Store.js) and merges the patch in,
        // rather than needing the key itself.
        case Actions.UPDATE_DESIGN_PAGE: {
            const patch = action.payload;
            const designPages = {...state.designPages};
            for (const key of Object.keys(designPages)) {
                if (designPages[key]?.design_page?.id === patch.id) {
                    designPages[key] = {...designPages[key], design_page: patch};
                }
            }
            return {
                ...state, designPages: designPages
            };
        }

        case Actions.SET_PROJECTS: {
            return {
                ...state, projects: action.payload
            };
        }

        case Actions.SET_SWIMLANES: {
            return {
                ...state, swimlanes: action.payload
            };
        }

        /*
         * `tickets` holds the Backlog page's two halves in one array - the paged
         * Backlog column (no sprint) and the visible sprints' tickets - so each
         * of these cases replaces its own half and leaves the other alone. See
         * the note on the slice in Store.js.
         *
         * The three paged lists (this one, `bugs` and `supportTickets` further
         * down) then read the same way:
         *
         *  - SET_* replaces with the first page of a (possibly newly filtered)
         *    request, and takes the paging cursor with it.
         *  - APPEND_* adds the next page, keeping the `total` the first page
         *    reported - the server only counts once per set of filters.
         *  - ADD_* and UPDATE_* are single-row changes, made here or arriving
         *    as a live patch.
         *
         * Appends de-duplicate by id. A page boundary is not a fence: a row
         * created, closed or dragged between two requests can shift what falls
         * either side of the cursor, and a row arriving twice is a much better
         * failure than React rendering duplicate keys.
         */
        case Actions.SET_BACKLOG_TICKETS: {
            return {
                ...state,
                tickets: [
                    ...state.tickets.filter((ticket) => ticket.sprintId != null),
                    ...action.payload.items,
                ],
                backlogPaging: firstPagePaging(action.payload),
            };
        }

        case Actions.APPEND_BACKLOG_TICKETS: {
            const known = new Set(state.tickets.map((ticket) => ticket.id));
            return {
                ...state,
                tickets: [
                    ...state.tickets,
                    ...action.payload.items.filter((ticket) => !known.has(ticket.id)),
                ],
                backlogPaging: nextPagePaging(action.payload, state.backlogPaging),
            };
        }

        // The sprint columns, whole rather than paged - bounded by the sprints
        // actually on screen (see SprintBacklog.js's visibleSprints).
        case Actions.SET_SPRINT_TICKETS: {
            return {
                ...state,
                tickets: [...state.tickets.filter((ticket) => ticket.sprintId == null), ...action.payload],
            };
        }

        case Actions.UPDATE_TASK: {
            const patch = action.payload;
            const existing = state.tickets.find((ticket) => ticket.id === patch.id) || null;
            const nowBacklog = patch.sprintId == null;

            // A closed ticket drops out of every list this slice feeds (the
            // Kanban board, the Backlog) right away, rather than sitting there
            // until the next reload - the same rule the server's own queries
            // enforce (see lib/listFilters.js and friends). A new array
            // reference either way, even when the ticket was never in this
            // slice at all (a Kanban-board ticket, mid-sprint, is never in
            // state.tickets - see the comment below): that reference changing
            // is the only signal pages/Project.js's sprint-tickets effect has
            // to re-read the board, and the closed ticket is exactly what it
            // would otherwise keep showing.
            if (patch.open === false) {
                const tickets = existing
                    ? state.tickets.filter((ticket) => ticket.id !== patch.id)
                    : [...state.tickets];

                let total = state.backlogPaging.total;
                if (total !== null && existing && existing.sprintId == null) total -= 1;

                return {...state, tickets, backlogPaging: {...state.backlogPaging, total}};
            }

            // A patch for a ticket this session hasn't loaded still has to
            // produce a new array: that reference changing is what tells the
            // Kanban board (see pages/Project.js) its sprint needs re-reading.
            // And when such a ticket has just become a backlog ticket, adding it
            // is better than letting it vanish - the client-side filter
            // predicate on the Backlog page decides whether it's shown.
            const tickets = existing
                ? state.tickets.map((ticket) => (ticket.id === patch.id ? patch : ticket))
                : (nowBacklog ? [...state.tickets, patch] : [...state.tickets]);

            /*
             * Keep the count honest for the one change it can be sure about: a
             * ticket already loaded here crossing between the backlog and a
             * sprint. A ticket we'd never seen is deliberately not counted - it
             * may well already be in the server's total, sitting beyond the page
             * that's loaded, and guessing would be worse than being one short
             * until the next fetch.
             */
            let total = state.backlogPaging.total;
            if (total !== null && existing) {
                const wasBacklog = existing.sprintId == null;
                if (wasBacklog && !nowBacklog) total -= 1;
                else if (!wasBacklog && nowBacklog) total += 1;
            }

            return {...state, tickets, backlogPaging: {...state.backlogPaging, total}};
        }

        // A ticket created here - New Task, or a promoted bug/support ticket.
        // Appended rather than prepended because the Backlog page orders each
        // column by sprintOrder and the server gives a new ticket the highest one
        // in its column: it goes at the bottom, where a reload would also put it.
        case Actions.ADD_TASK: {
            // "New Task" can create a ticket straight into a sprint, so this is
            // not always a backlog ticket.
            const intoBacklog = action.payload.sprintId == null;
            return {
                ...state,
                tickets: [...state.tickets, action.payload],
                backlogPaging: {
                    ...state.backlogPaging,
                    total: (state.backlogPaging.total === null || !intoBacklog)
                        ? state.backlogPaging.total
                        : state.backlogPaging.total + 1,
                },
            };
        }

        case Actions.ADD_SWIMLANE: {
            const newSwimlanes = state.swimlanes.slice();
            newSwimlanes.push({});
            return {
                ...state, swimlanes: newSwimlanes
            };
        }
        case Actions.MOVE_TASK: {
            const newSwimlanes = state.swimlanes.slice();
            const {old_swimlane, old_task_index, new_swimlane, new_task_index} = action.payload;
            const oldTask = newSwimlanes[old_swimlane].tasks[old_task_index];

            newSwimlanes[old_swimlane].tasks.splice(old_task_index, 1);
            newSwimlanes[new_swimlane].tasks.splice(new_task_index, 0, oldTask);

            return {
                ...state, swimlanes: [
                    ...newSwimlanes
                ]
            };
        }

        case Actions.MOVE_BACKLOG_TASK: {
            const newBacklogTasks = state.backlog.tasks.slice();
            const {old_task_index, new_task_index} = action.payload;
            const oldTask = newBacklogTasks[old_task_index];

            newBacklogTasks.splice(old_task_index, 1);
            newBacklogTasks.splice(new_task_index, 0, oldTask);

            return {
                ...state, backlog: {
                    ...state.backlog, tasks: [
                        ...newBacklogTasks
                    ]
                }
            };
        }

        case Actions.MOVE_TASK_FROM_BACKLOG: {
            const newSwimlanes = state.swimlanes.slice();
            const newBacklogTasks = state.backlog.tasks.slice();

            const {old_task_index, new_swimlane, new_task_index} = action.payload;
            const oldTask = newBacklogTasks[old_task_index];
            //splice task into backlog

            newBacklogTasks.splice(old_task_index, 1);
            newSwimlanes[new_swimlane].tasks.splice(new_task_index, 0, oldTask);

            return {
                ...state, swimlanes: [
                    ...newSwimlanes
                ], backlog: {
                    ...state.backlog, tasks: [
                        ...newBacklogTasks
                    ]
                }
            }
        }

        case Actions.MOVE_TASK_TO_BACKLOG: {
            const newSwimlanes = state.swimlanes.slice();
            const newBacklogTasks = state.backlog.tasks.slice();

            const {old_swimlane, old_task_index, new_task_index} = action.payload;
            const oldTask = newSwimlanes[old_swimlane].tasks[old_task_index];
            //splice task into backlog

            newSwimlanes[old_swimlane].tasks.splice(old_task_index, 1);
            newBacklogTasks.splice(new_task_index, 0, oldTask);

            return {
                ...state, swimlanes: [
                    ...newSwimlanes
                ], backlog: {
                    ...state.backlog, tasks: [
                        ...newBacklogTasks
                    ]
                }
            }
        }

        case Actions.SET_SPRINTS: {
            return {
                ...state, ...action.payload
            }
        }
        case Actions.ADD_SPRINT: {
            return {
                ...state,
                sprints: [...state.sprints, action.payload],
                current_sprint: (state.current_sprint === -1 || state.current_sprint == null) ? action.payload.id : state.current_sprint,
                selected_sprint_id: state.selected_sprint_id ?? action.payload.id,
            }
        }
        // Payload is a sprint (its `id` matching an existing entry in
        // state.sprints) plus an optional `current_sprint`, set whenever
        // starting/completing it also moved the project's own current-sprint
        // pointer - same flat shape the "entity:patched" realtime broadcast
        // sends (see Project.js), so a live patch from another user and the
        // direct response to this user's own Start/Complete call both flow
        // through here unchanged.
        case Actions.UPDATE_SPRINT: {
            const { current_sprint, ...sprint } = action.payload;
            return {
                ...state,
                sprints: state.sprints.map((s) => (s.id === sprint.id ? { ...s, ...sprint } : s)),
                current_sprint: current_sprint !== undefined ? current_sprint : state.current_sprint,
            }
        }
        case Actions.SET_SELECTED_SPRINT: {
            return {
                ...state, selected_sprint_id: action.payload
            }
        }
        case Actions.OPEN_TASK: {
            return {
                ...state, task: action.payload
            };
        }
        case Actions.CLOSE_TASK: {
            return {
                ...state, task: -1
            };
        }
        case Actions.SET_TICKET_TEMPLATE: {
            return {
                ...state, ticketTemplate: action.payload
            };
        }

        case Actions.SET_BUG_TEMPLATE: {
            return {
                ...state, bugTemplate: action.payload
            };
        }

        case Actions.SET_SCOPE: {
            return {
                ...state, scope: action.payload
            }
        }

        case Actions.SET_SCOPE_TYPES: {
            return {
                ...state, scope: {phase: state.scope.phase, scope_types: action.payload}
            }
        }

        case Actions.SET_SCOPE_TYPE_NAME: {
            const scope = state.scope;
            for(const s of state.scope.scope_types) {
                if(s.id === action.payload.id) {
                    s.name = action.payload.data;
                }
            }

            return {
                ...state, scope: scope
            }
        }

        case Actions.REMOVE_SCOPE_TYPE: {
            return {
                ...state, scope: {
                    phase: state.scope.phase,
                    scope_types: state.scope.scope_types.filter((s) => s.id !== action.payload)
                }
            }
        }

        case Actions.SET_PHASES: {
            return {
                ...state, scope: {phase: action.payload, scope_types: state.scope.scope_types}
            }
        }

        case Actions.UPDATE_PHASE: {
            const phase = sortPhases(state.scope.phase.map((p) => p.id === action.payload.id ? action.payload : p));

            return {
                ...state, scope: {phase: phase, scope_types: state.scope.scope_types}
            }
        }

        case Actions.ADD_PHASE: {
            return {
                ...state, scope: {
                    phase: sortPhases([...state.scope.phase, action.payload]),
                    scope_types: state.scope.scope_types
                }
            }
        }

        case Actions.REMOVE_PHASE: {
            return {
                ...state, scope: {
                    phase: state.scope.phase.filter((p) => p.id !== action.payload),
                    scope_types: state.scope.scope_types
                }
            }
        }

        case Actions.ADD_SCOPE_ITEMS: {
            const scope_items = state.scope_items;
            scope_items[action.payload.scope_type] = action.payload.data;

            return {
                ...state, scope_items: scope_items
            }
        }

        case Actions.REMOVE_SCOPE_ITEM: {
            const scope_items = state.scope_items;
            const list = scope_items[action.payload.scope_type] || [];
            scope_items[action.payload.scope_type] = list.filter((item) => item.id !== action.payload.id);

            return {
                ...state, scope_items: scope_items
            }
        }

        case Actions.MOVE_SCOPE_ITEM: {
            const scope_items = state.scope_items;
            const {fromType, toType, item} = action.payload;
            const fromList = scope_items[fromType] || [];
            scope_items[fromType] = fromList.filter((i) => i.id !== item.id);
            const toList = scope_items[toType] || [];
            scope_items[toType] = [...toList, item];

            return {
                ...state, scope_items: scope_items
            }
        }

        // A live patch (see the realtime "entity:patched" listener in
        // Project.js) or this session's own save landing back through the
        // store - finds the item wherever it currently lives (it may have
        // moved to a different scope class) and replaces it in place rather
        // than appending, so an in-place edit doesn't reshuffle the list.
        // Skipped entirely for a scope class this session never loaded (see
        // the class comment on Actions - no point maintaining state for
        // something nobody's looking at).
        case Actions.UPDATE_SCOPE_ITEM: {
            const item = action.payload;
            const scope_items = {...state.scope_items};
            let placedInPlace = false;

            for (const key of Object.keys(scope_items)) {
                const list = scope_items[key] || [];
                const idx = list.findIndex((i) => i.id === item.id);
                if (idx === -1) continue;

                if (Number(key) === item.scopeTypeId) {
                    scope_items[key] = list.map((i, j) => (j === idx ? item : i));
                    placedInPlace = true;
                } else {
                    scope_items[key] = list.filter((i) => i.id !== item.id);
                }
            }

            if (!placedInPlace && item.scopeTypeId in scope_items) {
                scope_items[item.scopeTypeId] = [...scope_items[item.scopeTypeId], item];
            }

            return {
                ...state, scope_items: scope_items
            }
        }

        // Every field is optional here (the same "undefined means leave as
        // is" convention the API's own PUT routes use) - callers merge in
        // just the filter(s) that changed rather than resending all five.
        case Actions.SET_BACKLOG_FILTERS: {
            return {
                ...state, backlogFilters: {...state.backlogFilters, ...action.payload}
            }
        }

        // Same "undefined means leave as is" merge as SET_BACKLOG_FILTERS -
        // used both for a single field changing and, with every key given at
        // once, for resetting the whole draft back to blank (see
        // NewTicket.js's clearDraft).
        case Actions.SET_NEW_TASK_DRAFT: {
            return {
                ...state, newTaskDraft: {...state.newTaskDraft, ...action.payload}
            }
        }

        // See the comment on SET_BACKLOG_TICKETS above - these three work the
        // same way.
        case Actions.SET_BUGS: {
            return {
                ...state,
                bugs: action.payload.items,
                bugsPaging: firstPagePaging(action.payload),
            };
        }

        case Actions.APPEND_BUGS: {
            const known = new Set(state.bugs.map((bug) => bug.id));
            return {
                ...state,
                bugs: [...state.bugs, ...action.payload.items.filter((bug) => !known.has(bug.id))],
                bugsPaging: nextPagePaging(action.payload, state.bugsPaging),
            };
        }

        // Newest first, so a bug reported here belongs at the top.
        case Actions.ADD_BUG: {
            return {
                ...state,
                bugs: [action.payload, ...state.bugs],
                bugsPaging: {
                    ...state.bugsPaging,
                    total: state.bugsPaging.total === null ? null : state.bugsPaging.total + 1,
                },
            };
        }

        case Actions.UPDATE_BUG: {
            return {
                ...state,
                bugs: state.bugs.map((bug) => (bug.id === action.payload.id ? action.payload : bug)),
            };
        }

        /*
         * One bug fetched on its own, folded into whatever is already loaded -
         * what a link straight to a bug dispatches (see ReviewBugs.js) when
         * that bug isn't on the page the list happens to have read so far.
         *
         * Putting it in the list rather than holding it beside the list is the
         * point: everything downstream - the detail panel, a save, a realtime
         * patch from another member - already works on `state.bugs` and would
         * otherwise quietly skip a bug reached by link.
         *
         * Inserted by id descending to keep the list's newest-first order, and
         * `total` is left alone: the bug was always part of the project's
         * count, it just hadn't been read yet.
         */
        case Actions.MERGE_BUG: {
            if (state.bugs.some((bug) => bug.id === action.payload.id)) {
                return {
                    ...state,
                    bugs: state.bugs.map((bug) => (bug.id === action.payload.id ? action.payload : bug)),
                };
            }

            const at = state.bugs.findIndex((bug) => bug.id < action.payload.id);
            const index = at === -1 ? state.bugs.length : at;
            return {
                ...state,
                bugs: [...state.bugs.slice(0, index), action.payload, ...state.bugs.slice(index)],
            };
        }

        // See the comment on SET_BACKLOG_TICKETS above.
        case Actions.SET_SUPPORT_TICKETS: {
            return {
                ...state,
                supportTickets: action.payload.items,
                supportTicketsPaging: firstPagePaging(action.payload),
                // Only the first page carries them, same as `total`. Kept as-is
                // rather than defaulted to [] on a later page, so paging can't
                // empty the filter bar's own options.
                supportTicketLevels: action.payload.levels ?? state.supportTicketLevels,
            };
        }

        case Actions.APPEND_SUPPORT_TICKETS: {
            const known = new Set(state.supportTickets.map((ticket) => ticket.id));
            return {
                ...state,
                supportTickets: [
                    ...state.supportTickets,
                    ...action.payload.items.filter((ticket) => !known.has(ticket.id)),
                ],
                supportTicketsPaging: nextPagePaging(action.payload, state.supportTicketsPaging),
            };
        }

        case Actions.SET_PLAN_LIMITS: {
            return {
                ...state, planLimits: action.payload
            };
        }

        // Newest first, so a ticket logged here belongs at the top.
        case Actions.ADD_SUPPORT_TICKET: {
            return {
                ...state,
                supportTickets: [action.payload, ...state.supportTickets],
                supportTicketsPaging: {
                    ...state.supportTicketsPaging,
                    total: state.supportTicketsPaging.total === null ? null : state.supportTicketsPaging.total + 1,
                },
            };
        }

        case Actions.UPDATE_SUPPORT_TICKET: {
            return {
                ...state,
                supportTickets: state.supportTickets.map((ticket) => (
                    ticket.id === action.payload.id ? action.payload : ticket
                )),
            };
        }

        // Same "undefined means leave as is" convention as SET_BACKLOG_FILTERS.
        case Actions.SET_SUPPORT_FILTERS: {
            return {
                ...state, supportFilters: {...state.supportFilters, ...action.payload}
            }
        }

        // Same "undefined means leave as is" convention as SET_BACKLOG_FILTERS.
        case Actions.SET_BUG_FILTERS: {
            return {
                ...state, bugFilters: {...state.bugFilters, ...action.payload}
            }
        }

        case Actions.ADD_SCOPE_TYPE: {
            const scope_types = state.scope.scope_types;
            const filtered = scope_types.filter((s) => s.id === action.payload.id);
            if (filtered.length === 0) {
                scope_types.push(action.payload);
            }

            return {
                ...state, scope: {phase: state.scope.phase, scope_types: scope_types}
            }
        }

        default:
            return state;
    }
};

export default Reducer;